import { AppError } from "config/errors";
import { IInvoice, PaymentMethodPayload } from "config/types";
import {
  createBankAccountPaymentIntentMutation,
  createBankAccountSetupIntentMutation,
  createMultiplePaymentsMutation,
  createPaymentMethodMutation,
  ICreateMultiplePaymentsInput,
} from "gql/mutations/payment";
import { activePaymentMethodQuery } from "gql/queries/payment";
import { RootState } from "store";
import { logBranchPurchaseEvent } from "utils/branch";

import { createAsyncThunk } from "@reduxjs/toolkit";

export const createPaymentMethod = createAsyncThunk(
  "createPaymentMethod",
  async (
    { externalId, source, metadata = {}, setupIntentId }: PaymentMethodPayload,
    { rejectWithValue }
  ) => {
    try {
      const { data } = await createPaymentMethodMutation(
        externalId,
        source,
        metadata,
        setupIntentId
      );
      return data.createPaymentMethod.paymentMethod;
    } catch (error) {
      return rejectWithValue(AppError.from(error).toPlainObject());
    }
  }
);

export const createBankAccountPaymentIntent = createAsyncThunk(
  "createBankAccountPaymentIntent",
  async (invoiceId: string, { rejectWithValue }) => {
    try {
      const { data } = await createBankAccountPaymentIntentMutation(invoiceId);
      return data.createBankAccountPaymentIntent;
    } catch (error) {
      return rejectWithValue(AppError.from(error).toPlainObject());
    }
  }
);

export const createBankAccountSetupIntent = createAsyncThunk(
  "createBankAccountSetupIntent",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await createBankAccountSetupIntentMutation();
      return data.createBankAccountSetupIntent;
    } catch (error) {
      return rejectWithValue(AppError.from(error).toPlainObject());
    }
  }
);

export const getActivePaymentMethod = createAsyncThunk(
  "getActivePaymentMethod",
  async (_, { rejectWithValue }) => {
    try {
      const response = await activePaymentMethodQuery();
      return response.data.activePaymentMethod;
    } catch (error) {
      return rejectWithValue(AppError.from(error).toPlainObject());
    }
  }
);

interface ICreatePayment {
  invoiceId: IInvoice["id"];
  purchasePolicy?: boolean;
}

export const createPayment = createAsyncThunk(
  "createPayment",
  async ({ invoiceId, purchasePolicy = false }: ICreatePayment, { rejectWithValue, getState }) => {
    try {
      const { policy } = getState() as RootState;
      const { data } = await createMultiplePaymentsMutation({ invoiceIds: [invoiceId] });
      const payments = data.createMultiplePayments.payments;
      const {
        processed: [processedInvoice],
      } = payments;

      if (purchasePolicy && processedInvoice.transactionResult !== "failed") {
        const policyId = policy.id;
        const price = policy.accountsProduct.amount;
        const term = policy.termDuration;
        logBranchPurchaseEvent(policyId, price, term);
      }

      return payments;
    } catch (error) {
      return rejectWithValue(AppError.from(error).toPlainObject());
    }
  }
);

export const createMultiplePayments = createAsyncThunk(
  "createMultiplePayments",
  async (input: ICreateMultiplePaymentsInput, { rejectWithValue }) => {
    try {
      const { data } = await createMultiplePaymentsMutation(input);

      return data.createMultiplePayments.payments;
    } catch (error) {
      return rejectWithValue(AppError.from(error).toPlainObject());
    }
  }
);
